import {BasicHistoryListModel} from '../models/basic-history-list.model';
import {HttpParams} from '@angular/common/http';
import {IProjectService} from './project.service.interface';
import {Injectable} from '@angular/core';
import {ProjectModel} from '../models/project.model';
import {ProjectTCO} from '../models/tco-info';
import {ProjectsApprovalListItemModel} from '../models/projects-approval-list-item.model';
import {ProjectsListItemModel, ProjectsListItemPagingModel} from '../models/projects-list-item.model';
import {QuotesModel} from '../models/quotes.model';
import {UserContextService} from '../../../core/services/user-context.service';
import {ProjectListOrderByModel} from '../models/project-list-order-by.model';
import {ProjectListItemFilterModel} from '../models/project-list-item-filter.model';
import {DatePipe} from '@angular/common';
import {firstValueFrom} from 'rxjs';

@Injectable()
export class ProjectDealerAppService extends IProjectService {

    public constructor(
        private userContextService: UserContextService
    ) {
        super();
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/2762145809/GET+Project+by+Id+v1
    public async getProject(projectId: number, lockProject = true): Promise<ProjectModel> {
        const userId = this.userContextService.getUserId();
        const result = await firstValueFrom(
            this.httpClient.get<ProjectModel>(
                `${this.serverAddress}/v1/users/${userId}/projects/${projectId}`,
                {
                    headers: {
                        lockProject: lockProject ? 'true' : 'false'
                    }
                }
            )
        );
        return ProjectModel.fromJson(result);
    }


    // public async getProjectsPaging(size: number, page: number, orderByParams: ProjectListOrderByModel, statusFilterParams: string[]): Promise<ProjectsListItemPagingModel> {
    public async getProjectsPaging(size: number, page: number, orderByParams: ProjectListOrderByModel, filterParams: ProjectListItemFilterModel): Promise<ProjectsListItemPagingModel> {

        const userId = this.userContextService.getUserId();

        let orderString = '';

        if (orderByParams) {
            for (const item of orderByParams.orderBy) {
                orderString = orderString + '&orderBy=' + item.column + '.' + item.direction;

            }
        }

        const url = `${this.serverAddress}/v1/users/${userId}/projects?size=${size}&page=${page}${orderString}`;

        let params = new HttpParams();

        if (filterParams) {
            Object.keys(filterParams).forEach(key => {
                if (filterParams[key]) {
                    if (Array.isArray(filterParams[key])) {
                        filterParams[key].forEach((item: string | number | boolean) => {
                            params = params.append(key, item);
                        });
                    } else if (filterParams[key] instanceof Date) {
                        const datePipe = new DatePipe('de-DE');
                        const strValue = datePipe.transform(filterParams[key], 'yyyyMMdd');
                        params = params.append(key, strValue);
                    } else {
                        params = params.append(key, filterParams[key]);
                    }
                }
            });
        }


        const result = await firstValueFrom(
            this.httpClient.get<ProjectsListItemPagingModel>(
                url,
                {
                    params: params
                }
            )
        );

        return result as ProjectsListItemPagingModel || null;
    }


    public async getProjects(): Promise<ProjectsListItemModel[]> {

        const userId = this.userContextService.getUserId();

        const result = await firstValueFrom(
            this.httpClient.get<ProjectsListItemModel[]>(
                `${this.serverAddress}/v1/users/${userId}/projects`
            )
        );

        const projects = result['projects'] as ProjectsListItemModel[];

        if (projects?.length > 0) {
            // console.log('projects loaded from rest', projects);
            return projects.map(project => ProjectsListItemModel.fromJson(project));
        } else {
            // console.log('project list comes empty from rest', projects);
            return null;
        }

    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/2762276865/CREATE+Project+Draft+v1
    public async createProjectDraft(project: ProjectModel): Promise<void> {
        const userId = this.userContextService.getUserId();
        let tmpProject: any = {...project};

        ProjectModel.updateProjectModelForConsistence(tmpProject);
        tmpProject = ProjectModel.toJson(tmpProject);
        ProjectModel.clearProjectForDealerAppRest(tmpProject);


        await firstValueFrom(
            this.httpClient.post(
                `${this.serverAddress}/v1/users/${userId}/projects/drafts`,
                tmpProject
            )
        );
    }

    public async removeProject(projectId: number): Promise<void> {
        const userId = this.userContextService.getUserId();
        await firstValueFrom(
            this.httpClient.delete(
                `${this.serverAddress}/v1/users/${userId}/projects/${projectId}`
            )
        );
    }

    public async getProjectBasicHistory(projectId: number): Promise<BasicHistoryListModel> {
        const userId = this.userContextService.getUserId();
        return await firstValueFrom(
            this.httpClient.get<BasicHistoryListModel>(
                `${this.serverAddress}/v1/users/${userId}/projects/${projectId}/basicHistory`
            )
        );
    }

    public getProjectPriceApprovals(): Promise<ProjectsApprovalListItemModel[]> {
        throw new Error('getProjectPriceApprovals in not implemented in dealerApp, should not be used');
        // return undefined;
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/2762145819/UPDATE+Project+v1
    public async updateProject(project: ProjectModel): Promise<QuotesModel> {
        const userId = this.userContextService.getUserId();
        ProjectModel.updateProjectModelForConsistence(project);

        const tmpProject = ProjectModel.toJson(project);
        ProjectModel.clearProjectForDealerAppRest(tmpProject);

        // @ts-ignore
        delete tmpProject.status;

        if (project.id) {
            await firstValueFrom(
                this.httpClient.put(
                    `${this.serverAddress}/v1/users/${userId}/projects/${project.id}/`,
                    tmpProject
                )
            );
        } else {
            console.warn('project gonging to be created instead updating because of missing id - it should not happen, check code logic: ', tmpProject);
            await this.createProject(project);
        }
        return null;
    }

    public async createProject(project: ProjectModel): Promise<boolean> {
        const userId = this.userContextService.getUserId();
        ProjectModel.updateProjectModelForConsistence(project);

        const tmpProject = ProjectModel.toJson(project);
        ProjectModel.clearProjectForDealerAppRest(tmpProject);

        await firstValueFrom(
            this.httpClient.post(
                `${this.serverAddress}/v1/users/${userId}/projects`,
                tmpProject
            )
        );
        return true;
    }


    public async copyProject(projectId: number): Promise<{ id: number }> {
        const userId = this.userContextService.getUserId();
        return await firstValueFrom(
            this.httpClient.post<{ id: number }>(
                `${this.serverAddress}/v1/users/${userId}/projects/${projectId}/copy`,
                null
            )
        );
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/2762145829/UPDATE+Project+Draft+v1
    public async updateProjectDraft(project: ProjectModel): Promise<void> {
        const userId = this.userContextService.getUserId();

        let tmpProject: any = {...project};
        ProjectModel.updateProjectModelForConsistence(tmpProject);
        tmpProject = ProjectModel.toJson(tmpProject);
        ProjectModel.clearProjectForDealerAppRest(tmpProject);


        // console.log('project service - updateProjectDraft - going to save a project:', tmpProject);
        await firstValueFrom(
            this.httpClient.put(
                `${this.serverAddress}/v1/users/${userId}/projects/${project.id}/drafts`,
                tmpProject
            )
        );
    }


    public updateProjectStatusDetail(projectId: number, statusDetail: string, comment: string): Promise<void> {
        throw new Error('createProjectDraft is not implemented in dealerApp, should not be used');
    }

    public getQuotesForProject(projectId: number): Promise<QuotesModel> {
        throw new Error('getQuotesForProject in not implemented in dealerApp, should not be used');
    }

    public async unlockProjectById(projectId: number): Promise<void> {
        const userId = this.userContextService.getUserId();
        await firstValueFrom(
            this.httpClient.get(
                `${this.serverAddress}/v1/users/${userId}/projects/${projectId}/unlock`
            )
        );
    }

    public async getProjectTCO(projectId: number): Promise<ProjectTCO> {
        return null;
    }

}
